import React, { useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import type { useQuestionRowBaseSelection } from "@components/charts/scorecard-config/hooks/useQuestionRowBaseSelection";
import type { ScorecardBrand } from "@/models/Scorecard";
import SourceQuestionAccordion from "./SourceQuestionAccordion";
import GridValueOrRollupSelector from "./GridValueOrRollupSelector";
import MappingRow from "./MappingRow";
import UnavailableConfirmDialog from "./UnavailableConfirmDialog";
import { availableOptionsFor } from "@/utils/scorecard-utils";

interface NewQuestionBaseBuilderProps {
  rowBaseSelection: ReturnType<typeof useQuestionRowBaseSelection>;
}

/** New Question Base's own question + (for Single grid only) value/rollup
 *  picker — reuses the same "Percentage row definition flow" as a Question
 *  row's own metric. A Single/Multiple question's full set of response
 *  categories already forms the diagonal cross axis, so no value/rollup
 *  step shows for those — only Single grid needs one, to narrow its value
 *  axis. Brand matching runs automatically in the background (same
 *  auto-match algorithm as everywhere else) — only brands that didn't
 *  auto-match show up here, for manual resolution. Matched brands aren't
 *  listed at all, so this never duplicates the row's own Brand Mappings
 *  section below. */
const NewQuestionBaseBuilder: React.FC<NewQuestionBaseBuilderProps> = ({
  rowBaseSelection,
}) => {
  const {
    searchTerm,
    setSearchTerm,
    accordionExpanded,
    setAccordionExpanded,
    selectedQuestionId,
    questionDetail,
    eligibleQuestions,
    isFetchingQuestions,
    observerTarget,
    handleQuestionSelect,
    sourceQuestionKind,
    isVariableFocus,
    gridMode,
    handleGridModeChange,
    selectedValueId,
    handleValueChange,
    rollupName,
    setRollupName,
    rollupSelectedCodes,
    setRollupSelectedCodes,
    brands,
    mappings,
    responseCategories,
    onManualBind,
    onMarkUnavailable,
    onRestoreMapping,
  } = rowBaseSelection;

  const brandById = new Map(brands.map((b) => [b.id, b]));
  const [pendingUnavailableBrand, setPendingUnavailableBrand] = useState<ScorecardBrand | null>(
    null,
  );
  const [reason, setReason] = useState("");
  const needsAttention = mappings.filter((m) => m.status !== "matched");

  const handleConfirmUnavailable = () => {
    if (pendingUnavailableBrand) {
      onMarkUnavailable(pendingUnavailableBrand.id, reason.trim());
    }
    setPendingUnavailableBrand(null);
    setReason("");
  };

  return (
    <Box sx={{ mt: 1 }}>
      <SourceQuestionAccordion
        label="Base Question*"
        expanded={accordionExpanded}
        onExpandedChange={setAccordionExpanded}
        selectedQuestionLabel={questionDetail?.label}
        selectedQuestionTitle={questionDetail?.title}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        eligibleQuestions={eligibleQuestions}
        selectedQuestionId={selectedQuestionId}
        onQuestionSelect={handleQuestionSelect}
        isFetching={isFetchingQuestions}
        observerTarget={observerTarget}
        noResultsMessage="No compatible questions found. A New Question Base needs a Single, Multiple, or Single grid question with at least two brand-shaped response options."
      />

      {questionDetail && sourceQuestionKind === "single_grid" && (
        <Box sx={{ mt: 1.5 }}>
          <GridValueOrRollupSelector
            gridMode={gridMode}
            onGridModeChange={handleGridModeChange}
            values={questionDetail.values ?? []}
            selectedGridValueId={selectedValueId}
            onGridValueChange={handleValueChange}
            rollupName={rollupName}
            rollupSelectedLabels={rollupSelectedCodes}
            onRollupNameChange={setRollupName}
            onRollupLabelsChange={setRollupSelectedCodes}
            focusMode={isVariableFocus ? "variable" : "value"}
            variables={questionDetail.rowVariables ?? []}
          />
        </Box>
      )}

      {questionDetail && needsAttention.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 0.5 }}>
            {needsAttention.length} brand{needsAttention.length > 1 ? "s" : ""} couldn&apos;t be
            automatically matched — resolve below.
          </Typography>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            {needsAttention.map((m) => {
              const brand = brandById.get(m.brandId);
              if (!brand) return null;
              return (
                <MappingRow
                  key={brand.id}
                  brand={brand}
                  mapping={m}
                  availableOptions={availableOptionsFor(brand.id, mappings, responseCategories)}
                  onManualBind={onManualBind}
                  onMarkUnavailableClick={setPendingUnavailableBrand}
                  onRestoreMapping={onRestoreMapping}
                />
              );
            })}
          </Box>
        </Box>
      )}
      {questionDetail && needsAttention.length === 0 && mappings.length > 0 && (
        <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
          All brands matched automatically.
        </Alert>
      )}

      <UnavailableConfirmDialog
        brandLabel={pendingUnavailableBrand?.label}
        open={!!pendingUnavailableBrand}
        reason={reason}
        onReasonChange={setReason}
        onCancel={() => setPendingUnavailableBrand(null)}
        onConfirm={handleConfirmUnavailable}
      />
    </Box>
  );
};

export default NewQuestionBaseBuilder;
